import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useStripePayouts } from './use-stripe-payouts';
import { useCurrentFilmmakerProfile } from './use-filmmaker-banking';
import { usePayments } from './use-payments';

export const useStripeOnboarding = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { updateOnboardingStatus, createOnboardingLink } = useStripePayouts();
  const { data: filmmakerProfile } = useCurrentFilmmakerProfile();
  const { paymentMethods, addPaymentMethod, deletePaymentMethod } =
    usePayments();

  // Flag to prevent infinite loops when processing URL parameters
  const hasProcessedUrl = useRef(false);

  // Handle Stripe onboarding completion from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const setupComplete = urlParams.get('setup');
    const refreshParam = urlParams.get('refresh');

    // Only proceed if we have URL parameters indicating return from Stripe
    if (!setupComplete && !refreshParam) return;

    // Prevent processing the same URL multiple times
    if (hasProcessedUrl.current) {
      console.log('URL already processed, skipping...');
      return;
    }

    // Don't run if we don't have the necessary data yet
    if (!filmmakerProfile || !paymentMethods || paymentMethods.length === 0) {
      console.log(
        'Waiting for filmmaker profile and payment methods to load...'
      );
      return;
    }

    // Mark as processing to prevent re-runs
    hasProcessedUrl.current = true;

    const handleStripeReturn = async () => {
      console.log('Processing Stripe return with params:', {
        setupComplete,
        refreshParam,
      });

      if (setupComplete === 'complete') {
        // Handle return from Stripe onboarding completion
        console.log(
          'Returned from Stripe onboarding, checking actual status...'
        );

        // Find the most recently added bank account with Stripe account ID
        const recentBankAccount = paymentMethods.find(
          (method) =>
            method.stripe_account_id && !method.stripe_onboarding_completed
        );

        console.log('Found recent bank account:', recentBankAccount);

        if (recentBankAccount?.stripe_account_id) {
          updateOnboardingStatus.mutate(
            {
              accountId: recentBankAccount.stripe_account_id,
            },
            {
              onSuccess: (data) => {
                console.log('Onboarding status check result:', data);
                if (data.cleanup_performed) {
                  toast.info('Verification incomplete', {
                    description:
                      'Please add your bank account again to complete setup.',
                  });
                } else if (data.onboarding_completed) {
                  toast.success('Verification complete!', {
                    description:
                      'Your account is now set up to receive payments.',
                  });
                } else {
                  toast.info('Complete your verification', {
                    description:
                      'Please finish setting up your Stripe account to receive payments.',
                  });
                }
              },
              onError: (error) => {
                console.error('Onboarding status check failed:', error);
                toast.error('Verification check failed', {
                  description:
                    'Unable to verify your account status. Please try again.',
                });
              },
              onSettled: () => {
                // Refresh payment methods after status check to ensure UI is updated
                queryClient.invalidateQueries({
                  queryKey: ['paymentMethods', user?.id],
                });

                // Clean up URL after processing
                window.history.replaceState(
                  {},
                  document.title,
                  '/filmmaker/dashboard/earnings'
                );
              },
            }
          );
        } else {
          // No pending bank account found - user may have gone back without completing
          console.log('No recent bank account found with Stripe ID');
          toast.info('Setup verification needed', {
            description:
              'Please complete your Stripe verification to receive payments.',
          });

          // Clean up URL
          window.history.replaceState(
            {},
            document.title,
            '/filmmaker/dashboard/earnings'
          );
        }
      } else if (refreshParam === 'true') {
        // Handle refresh from Stripe - user needs to complete additional steps
        console.log('Returned from Stripe refresh, checking account status...');

        // Find any incomplete bank account and check its status
        const incompleteBankAccount = paymentMethods.find(
          (method) =>
            method.stripe_account_id && !method.stripe_onboarding_completed
        );

        console.log('Found incomplete bank account:', incompleteBankAccount);

        if (incompleteBankAccount?.stripe_account_id) {
          // Check if the account still exists and what its status is
          updateOnboardingStatus.mutate(
            {
              accountId: incompleteBankAccount.stripe_account_id,
            },
            {
              onSuccess: (data) => {
                console.log('Refresh status check result:', data);
                if (data.cleanup_performed) {
                  // Account was incomplete and cleaned up
                  toast.info('Verification incomplete', {
                    description:
                      'Please add your bank account again to complete setup.',
                  });
                } else if (!data.onboarding_completed) {
                  // Account exists but still needs completion
                  toast.info('Complete your verification', {
                    description:
                      'Please finish setting up your Stripe account to receive payments.',
                  });
                } else {
                  // Account is actually complete now
                  toast.success('Verification complete!', {
                    description:
                      'Your account is now set up to receive payments.',
                  });
                }
              },
              onError: (error) => {
                console.error('Refresh status check failed:', error);
                toast.error('Verification check failed', {
                  description:
                    'Unable to verify your account status. Please try again.',
                });
              },
              onSettled: () => {
                // Refresh payment methods after status check
                queryClient.invalidateQueries({
                  queryKey: ['paymentMethods', user?.id],
                });

                // Clean up URL after processing
                window.history.replaceState(
                  {},
                  document.title,
                  '/filmmaker/dashboard/earnings'
                );
              },
            }
          );
        } else {
          // No bank account with Stripe ID found
          console.log('No bank account with Stripe ID found');
          toast.info('Setup required', {
            description:
              'Please add your bank account to start receiving payments.',
          });

          // Clean up URL
          window.history.replaceState(
            {},
            document.title,
            '/filmmaker/dashboard/earnings'
          );
        }
      }
    };

    handleStripeReturn();
  }, [
    filmmakerProfile,
    paymentMethods,
    updateOnboardingStatus,
    queryClient,
    user?.id,
  ]);

  // Handle adding a new payment method with Stripe onboarding
  const handleAddPaymentMethodWithStripe = async (bankAccountData: {
    accountName: string;
    sortCode: string;
    accountNumber: string;
    isDefault: boolean;
  }) => {
    try {
      // First add the bank account to the database
      const newBankAccount = await addPaymentMethod.mutateAsync(
        bankAccountData
      );

      if (filmmakerProfile && newBankAccount) {
        try {
          // Create Stripe onboarding link
          const result = await createOnboardingLink.mutateAsync({
            filmmakerDetails: {
              email: filmmakerProfile.email,
              first_name: filmmakerProfile.first_name,
              last_name: filmmakerProfile.last_name,
            },
            bankDetails: {
              account_name: bankAccountData.accountName,
              account_number: bankAccountData.accountNumber,
              sort_code: bankAccountData.sortCode,
              banking_id: newBankAccount.banking_id,
            },
            filmmakerId: filmmakerProfile.filmmaker_id,
            return_url: `${window.location.origin}/filmmaker/dashboard/earnings?setup=complete`,
            refresh_url: `${window.location.origin}/filmmaker/dashboard/earnings?refresh=true`,
          });

          // Open Stripe onboarding in a new tab
          if (result.onboarding_url) {
            window.open(result.onboarding_url, '_blank');
            toast.success('Bank account added - Complete Stripe setup', {
              description:
                'Please complete your verification in the new tab to receive payments.',
            });
          }

          return { success: true, newBankAccount };
        } catch (stripeError) {
          // If Stripe onboarding creation fails, we should clean up the bank account
          console.error(
            'Stripe onboarding creation failed, cleaning up bank account:',
            stripeError
          );

          // Attempt to delete the bank account we just created
          try {
            if (newBankAccount?.banking_id) {
              await deletePaymentMethod.mutateAsync(newBankAccount.banking_id);
              console.log('Cleaned up bank account after Stripe failure');
            }
          } catch (cleanupError) {
            console.error(
              'Failed to cleanup bank account after Stripe error:',
              cleanupError
            );
            // Even if cleanup fails, still refresh to show current state
            queryClient.invalidateQueries({
              queryKey: ['paymentMethods', user?.id],
            });
          }

          toast.error('Setup failed', {
            description:
              'Failed to create Stripe onboarding. Bank account has been removed.',
          });
          throw stripeError;
        }
      } else {
        toast.success('Bank account added', {
          description:
            'Account added but Stripe integration may need manual setup.',
        });
        return { success: true, newBankAccount };
      }
    } catch (error) {
      console.error('Failed to add payment method with Stripe:', error);
      toast.error('Setup failed', {
        description: 'Failed to add bank account. Please try again.',
      });
      throw error;
    }
  };

  return {
    handleAddPaymentMethodWithStripe,
    isCreatingOnboardingLink: createOnboardingLink.isPending,
    isUpdatingOnboardingStatus: updateOnboardingStatus.isPending,
  };
};
